import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Send, Gift, Heart, Settings, Save, Clock, Sparkles, Gamepad2, Globe, Brain, ExternalLink, Camera, Cookie } from "lucide-react";
import { toast } from "sonner";

const AI_AVATAR = "https://images.unsplash.com/photo-1657641898365-48ae7d64e676?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxjdXRlJTIwYWklMjBhdmF0YXIlMjByb2JvdHxlbnwwfHx8fDE3ODc3NzM2ODB8MA&ixlib=rb-4.1.0&q=85";

export default function AIDaughter() {
  const [username, setUsername] = useState("random_rob");
  const [message, setMessage] = useState("");
  const [state, setState] = useState({ mood_score: 10, emotion: "neutral", emotion_info: { emoji: "🙂", color: "#eab308", label: "Neutral" } });
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [persona, setPersona] = useState(null);
  const [savingPersona, setSavingPersona] = useState(false);
  const [live, setLive] = useState(false);
  const [stash, setStash] = useState({ cookies: 0, cupcakes: 0 });
  const [sneak, setSneak] = useState({ min_minutes: 10, max_minutes: 20, exclude_mods: false, exclude_users: [] });
  const [econ, setEcon] = useState({ bribe_cooldown_sec: 120 });
  const endRef = useRef(null);
  const avatarRef = useRef(null);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post("/config/persona", { avatar_url: r.data.url });
      setPersona(res.data);
      toast.success("Profile picture updated! 💖");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const loadState = async () => {
    const s = await api.get("/ai/state");
    setState(s.data);
  };
  const loadCfg = async () => {
    const r = await api.get("/config/payout");
    setCfg(r.data);
  };
  const loadPersona = async () => {
    const r = await api.get("/config/persona");
    setPersona(r.data);
  };
  const loadThread = async (u) => {
    const r = await api.get(`/ai/conversation/${u}`);
    setThread(r.data.messages || []);
  };

  useEffect(() => { loadState(); loadCfg(); loadPersona(); }, []);
  useEffect(() => {
    const f = () => api.get("/twitch/live").then((r) => setLive(!!r.data.live)).catch(() => {});
    f();
    const t = setInterval(f, 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const f = () => api.get("/stash").then((r) => setStash(r.data)).catch(() => {});
    f();
    api.get("/config/sneak").then((r) => setSneak(r.data)).catch(() => {});
    api.get("/config/econ").then((r) => setEcon(r.data)).catch(() => {});
    const t = setInterval(f, 20000);
    return () => clearInterval(t);
  }, []);
  const saveSneak = async (next) => { setSneak(next); try { await api.post("/config/sneak", next); } catch (e) {} };
  const saveStash = async () => { try { const r = await api.post("/stash", { cookies: stash.cookies, cupcakes: stash.cupcakes }); setStash(r.data); toast.success("Stash updated"); } catch (e) { toast.error("Couldn't save stash"); } };
  useEffect(() => { loadThread(username); }, [username]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread]);

  const send = async () => {
    if (!message.trim()) return;
    setLoading(true);
    const text = message;
    setMessage("");
    setThread((t) => [...t, { role: "user", text }]);
    try {
      const res = await api.post("/ai/talk", { username, message: text });
      setThread((t) => [...t, { role: "sprinkles", text: res.data.reply, sources: res.data.sources || [] }]);
      setState({ mood_score: res.data.mood_score, emotion: res.data.emotion, emotion_info: res.data.emotion_info });
      if (res.data.learned_note) {
        toast.success(`PancakeCupCake_ learned: ${res.data.learned_note}`, { duration: 6000 });
        loadPersona();
      }
      if (res.data.mood_delta > 0) toast.success(`PancakeCupCake_ is happier! +${res.data.mood_delta} mood`);
      else if (res.data.mood_delta < 0) toast.error(`You upset PancakeCupCake_... ${res.data.mood_delta} mood`);
    } catch (e) {
      toast.error("PancakeCupCake_ couldn't reply");
    } finally {
      setLoading(false);
    }
  };

  const setMood = async (val) => {
    const res = await api.post("/ai/mood", { mood_score: val });
    setState(res.data);
  };

  const payout = async () => {
    const res = await api.post("/ai/payout");
    toast.success(res.data.message);
  };

  const saveCfg = async () => {
    const res = await api.post("/config/payout", cfg);
    setCfg(res.data);
    toast.success("Payout settings saved");
  };

  const savePersona = async () => {
    setSavingPersona(true);
    try {
      const res = await api.post("/config/persona", persona);
      setPersona(res.data);
      toast.success("PancakeCupCake_'s personality saved!");
    } catch (e) {
      toast.error("Couldn't save personality");
    } finally { setSavingPersona(false); }
  };

  const info = state.emotion_info || { emoji: "🙂", color: "#eab308", label: "Neutral" };
  const MOODS = [
    { key: "ecstatic", label: "Ecstatic 🤩", color: "#22c55e" },
    { key: "happy", label: "Happy 😊", color: "#84cc16" },
    { key: "neutral", label: "Neutral 🙂", color: "#eab308" },
    { key: "sad", label: "Sad 🥺", color: "#f97316" },
    { key: "mad", label: "Mad 😡", color: "#ef4444" },
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-800 font-heading">PancakeCupCake_</h1>
        <p className="text-muted-foreground mt-1 text-sm">Your bot's little daughter — she knows her family, remembers everyone, and has real feelings.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Emotion / mood panel */}
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 flex flex-col items-center text-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full blur-xl opacity-60" style={{ background: info.color }} />
            <img
              src={persona?.avatar_url ? (persona.avatar_url.startsWith("http") ? persona.avatar_url : window.location.origin + persona.avatar_url) : AI_AVATAR}
              alt="PancakeCupCake_"
              className="relative w-28 h-28 rounded-full object-cover border-4"
              style={{ borderColor: info.color }}
              data-testid="ai-avatar"
            />
            <button
              type="button"
              onClick={() => avatarRef.current?.click()}
              className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center border-2 border-[#0a0a0a] transition-colors"
              title="Change picture"
              data-testid="avatar-upload-button"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={uploadAvatar} data-testid="avatar-file-input" />
          </div>
          <div className="mt-4 flex items-center gap-2" data-testid="emotion-badge">
            <span className="text-3xl">{info.emoji}</span>
            <span className="text-xl font-heading font-700" style={{ color: info.color }}>{info.label}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Mood score: {state.mood_score}</p>

          <div className="w-full mt-6">
            <label className="text-xs text-muted-foreground">Adjust mood manually</label>
            <Slider
              data-testid="mood-slider"
              value={[state.mood_score]}
              min={-100}
              max={100}
              step={5}
              onValueChange={(v) => setState((s) => ({ ...s, mood_score: v[0] }))}
              onValueCommit={(v) => setMood(v[0])}
              className="mt-3"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>😡 Mad</span><span>🙂 Neutral</span><span>🤩 Ecstatic</span>
            </div>
          </div>

          <Button onClick={payout} data-testid="mood-payout-button" className="mt-6 w-full bg-cookie text-black hover:bg-cookie/90 font-semibold rounded-lg">
            <Gift className="w-4 h-4 mr-2" /> Mood Cookie Payout
          </Button>
          <p className="text-[11px] text-muted-foreground mt-2">Happier PancakeCupCake_ = bigger gifts for everyone.</p>
        </div>

        {/* Chat panel */}
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 rounded-lg flex flex-col h-[560px]">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 bg-black/40">
            <Heart className="w-4 h-4 text-primary" />
            <span className="text-sm font-heading font-700">Chat as</span>
            <Input
              data-testid="ai-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-7 w-40 bg-secondary border-white/10 text-sm"
            />
            <span className="text-xs text-muted-foreground ml-auto">Memory active 🧠</span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-thread">
            {thread.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Say hi to PancakeCupCake_!</p>}
            {thread.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-fade-up`}>
                <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${
                  m.role === "user" ? "bg-secondary rounded-br-sm" : "bg-primary/15 border border-primary/30 rounded-bl-sm"
                }`}>
                  {m.role !== "user" && <span className="block text-[10px] text-primary font-semibold mb-0.5">PancakeCupCake_</span>}
                  {m.text}
                  {m.sources?.length > 0 && (
                    <div className="mt-1.5 pt-1.5 border-t border-white/10 flex flex-wrap gap-2" data-testid="reply-sources">
                      <span className="text-[10px] text-muted-foreground">sources:</span>
                      {m.sources.map((u, j) => (
                        <a key={j} href={u} target="_blank" rel="noreferrer" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5 max-w-[160px] truncate">
                          <ExternalLink className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{u.replace(/^https?:\/\//, "")}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && <div className="text-primary text-sm animate-pulse">PancakeCupCake_ is typing…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <Input
              data-testid="ai-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Talk to PancakeCupCake_… be nice (or not 😈)"
              className="flex-1 bg-secondary border-white/10 focus:ring-primary"
            />
            <Button data-testid="ai-send-button" onClick={send} disabled={loading} className="bg-primary hover:bg-primary/90 rounded-lg">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Personality editor */}
      {persona && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6" data-testid="persona-settings">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="font-heading font-700 text-lg">Personality</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-5">Describe how PancakeCupCake_ acts. This shapes every reply. (Her game knowledge lives in the <span className="text-primary">Game Help</span> tab.)</p>

          <div>
            <label className="text-xs text-muted-foreground">How she acts (personality &amp; description)</label>
            <Textarea
              data-testid="persona-personality"
              value={persona.personality}
              onChange={(e) => setPersona((p) => ({ ...p, personality: e.target.value }))}
              rows={5}
              placeholder="e.g. You are PancakeCupCake_, a cute 7-year-old daughter who loves cupcakes and talks in short playful sentences…"
              className="bg-secondary border-white/10 mt-1 text-sm resize-y"
            />
          </div>

          <div className="flex justify-end mt-5">
            <Button onClick={savePersona} disabled={savingPersona} data-testid="save-persona-button" className="bg-primary hover:bg-primary/90 rounded-lg">
              <Save className="w-4 h-4 mr-2" />{savingPersona ? "Saving…" : "Save Personality"}
            </Button>
          </div>
        </div>
      )}

      {/* Her stash + sneak frequency */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6" data-testid="stash-panel">
        <div className="flex items-center gap-2 mb-4">
          <Cookie className="w-4 h-4 text-cookie" />
          <h3 className="font-heading font-700 text-lg">Her Stash</h3>
          {stash.cookies <= 0 && stash.cupcakes <= 0 && <span className="text-[11px] text-red-400">empty — she's sad 🥺</span>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl bg-secondary p-4 text-center" data-testid="stash-cookies">
            <div className="text-3xl">🍪🫙</div>
            <div className="text-[11px] text-muted-foreground mb-1 mt-1">Cookie jar</div>
            <Input type="number" value={stash.cookies} onChange={(e) => setStash((s) => ({ ...s, cookies: parseInt(e.target.value) || 0 }))} className="bg-[#0a0a0a] border-white/10 text-center h-8" data-testid="stash-cookies-input" />
          </div>
          <div className="rounded-xl bg-secondary p-4 text-center" data-testid="stash-cupcakes">
            <div className="text-3xl">🧁🍽️</div>
            <div className="text-[11px] text-muted-foreground mb-1 mt-1">Cupcake tray</div>
            <Input type="number" value={stash.cupcakes} onChange={(e) => setStash((s) => ({ ...s, cupcakes: parseInt(e.target.value) || 0 }))} className="bg-[#0a0a0a] border-white/10 text-center h-8" data-testid="stash-cupcakes-input" />
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button size="sm" onClick={saveStash} className="bg-primary hover:bg-primary/90" data-testid="save-stash-button"><Save className="w-4 h-4 mr-1" />Save stash</Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">She fills these when treats are gifted or she sneaks them. Empty jar + tray makes her sad. Viewers <span className="text-white font-mono">!bribe</span> to get some back.</p>

        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-primary" /><span className="text-sm font-medium">Sneak frequency (while live)</span></div>
          <p className="text-[11px] text-muted-foreground mb-3">She tries to sneak treats every {sneak.min_minutes}–{sneak.max_minutes} minutes.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] text-muted-foreground">Min: {sneak.min_minutes} min</label>
              <Slider value={[sneak.min_minutes]} min={1} max={60} step={1} onValueChange={(v) => setSneak((s) => ({ ...s, min_minutes: v[0] }))} onValueCommit={(v) => saveSneak({ ...sneak, min_minutes: v[0], max_minutes: Math.max(sneak.max_minutes, v[0]) })} className="mt-2" data-testid="sneak-min-slider" />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Max: {sneak.max_minutes} min</label>
              <Slider value={[sneak.max_minutes]} min={1} max={120} step={1} onValueChange={(v) => setSneak((s) => ({ ...s, max_minutes: Math.max(v[0], s.min_minutes) }))} onValueCommit={(v) => saveSneak({ ...sneak, max_minutes: Math.max(v[0], sneak.min_minutes) })} className="mt-2" data-testid="sneak-max-slider" />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3 mt-4">
            <div><span className="text-sm">Don't sneak from mods</span><p className="text-[11px] text-muted-foreground">Exclude moderators as sneak targets</p></div>
            <Switch checked={!!sneak.exclude_mods} onCheckedChange={(v) => saveSneak({ ...sneak, exclude_mods: v })} data-testid="sneak-exclude-mods" />
          </div>
          <div className="mt-3">
            <label className="text-[11px] text-muted-foreground">Never sneak from (comma-separated usernames)</label>
            <Input value={(sneak.exclude_users || []).join(", ")} onChange={(e) => setSneak((s) => ({ ...s, exclude_users: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))} onBlur={() => saveSneak(sneak)} placeholder="nightbot, streamelements" className="bg-secondary border-white/10 mt-1 h-9" data-testid="sneak-exclude-users" />
          </div>
          <div className="mt-3">
            <label className="text-[11px] text-muted-foreground">Bribe cooldown: {econ.bribe_cooldown_sec}s</label>
            <Slider value={[econ.bribe_cooldown_sec]} min={0} max={600} step={10} onValueChange={(v) => setEcon({ bribe_cooldown_sec: v[0] })} onValueCommit={(v) => { setEcon({ bribe_cooldown_sec: v[0] }); api.post("/config/econ", { bribe_cooldown_sec: v[0] }); }} className="mt-2" data-testid="bribe-cooldown-slider" />
          </div>
        </div>
      </div>

      {/* Mood payout settings */}
      {cfg && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6" data-testid="payout-settings">
          <div className="flex items-center gap-2 mb-1">
            <Settings className="w-4 h-4 text-cookie" />
            <h3 className="font-heading font-700 text-lg">Mood Payout Settings</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-5">PancakeCupCake_ auto-gifts everyone on a timer. Set how much she gives at each mood.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Amount per mood</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-2">
                {MOODS.map((m) => (
                  <div key={m.key}>
                    <label className="text-xs" style={{ color: m.color }}>{m.label}</label>
                    <Input
                      type="number"
                      data-testid={`payout-amount-${m.key}`}
                      value={cfg.amounts[m.key]}
                      onChange={(e) => { const val = parseInt(e.target.value) || 0; setCfg((prev) => ({ ...prev, amounts: { ...prev.amounts, [m.key]: val } })); }}
                      className="bg-secondary border-white/10 mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Every (minutes)</label>
                <Input
                  type="number"
                  data-testid="payout-interval"
                  value={cfg.interval_minutes}
                  onChange={(e) => { const val = parseInt(e.target.value) || 1; setCfg((prev) => ({ ...prev, interval_minutes: val })); }}
                  className="bg-secondary border-white/10 mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Currency</label>
                <div className={`flex gap-2 mt-1 ${cfg.random_currency ? "opacity-40 pointer-events-none" : ""}`}>
                  <button onClick={() => setCfg((prev) => ({ ...prev, currency: "cookies" }))} data-testid="payout-currency-cookies" className={`flex-1 rounded-lg py-2 text-sm transition-colors duration-200 ${cfg.currency === "cookies" ? "bg-cookie text-black" : "bg-secondary text-muted-foreground"}`}>🍪 Cookies</button>
                  <button onClick={() => setCfg((prev) => ({ ...prev, currency: "cupcakes" }))} data-testid="payout-currency-cupcakes" className={`flex-1 rounded-lg py-2 text-sm transition-colors duration-200 ${cfg.currency === "cupcakes" ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>🧁 Cupcakes</button>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
                <div>
                  <label className="text-sm">Randomly pick treat</label>
                  <p className="text-[11px] text-muted-foreground">She surprises chat with cookies OR cupcakes each time</p>
                </div>
                <Switch checked={cfg.random_currency} onCheckedChange={(v) => setCfg((prev) => ({ ...prev, random_currency: v }))} data-testid="payout-random-toggle" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
                <div>
                  <label className="text-sm">Auto payout</label>
                  <p className="text-[11px] text-muted-foreground">Gift on the timer automatically</p>
                </div>
                <Switch checked={cfg.auto_enabled} onCheckedChange={(v) => setCfg((prev) => ({ ...prev, auto_enabled: v }))} data-testid="payout-auto-toggle" />
              </div>
              <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
                <div>
                  <label className="text-sm flex items-center gap-1.5">Only when live
                    <span className={`w-2 h-2 rounded-full ${live ? "bg-green-500 animate-pulse" : "bg-gray-500"}`} title={live ? "LIVE" : "offline"} data-testid="live-indicator" />
                  </label>
                  <p className="text-[11px] text-muted-foreground">{live ? "You're LIVE — auto payouts running" : "Auto-detected via Twitch; pauses when offline"}</p>
                </div>
                <Switch checked={cfg.require_live} onCheckedChange={(v) => setCfg((prev) => ({ ...prev, require_live: v }))} data-testid="payout-requirelive-toggle" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <Button onClick={payout} variant="outline" className="border-white/10 rounded-lg" data-testid="test-payout-button"><Gift className="w-4 h-4 mr-2" />Test Payout Now</Button>
            <Button onClick={saveCfg} data-testid="save-payout-button" className="bg-primary hover:bg-primary/90 rounded-lg"><Save className="w-4 h-4 mr-2" />Save Settings</Button>
          </div>
        </div>
      )}
    </div>
  );
}
